import React, { ChangeEvent, useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { faDownload, faPaperPlane, faUpload } from '@fortawesome/free-solid-svg-icons'
import { faTrashCan } from '@fortawesome/free-solid-svg-icons/faTrashCan'
import Button, { ButtonSize, ButtonTheme } from 'components/Form/Button'
import ModalAction from 'components/Modal/ModalAction'
import { AppContext } from 'contexts/AppContext'
import { Disposition } from 'types/disposition'
import service from '../service'

interface InvoiceActionsProps {
  disposition: Disposition
  onChanged: () => void
  /** The client only downloads the invoice - attaching, sending and removing stay with AMS users. */
  readOnly?: boolean
}

/**
 * An invoice delivered by the mailbox job has already been emailed, so it only gets a download.
 * When the job did not attach one, the user uploads the pdf and sends it once in a separate step.
 */
const InvoiceActions: React.FC<InvoiceActionsProps> = ({
  disposition,
  onChanged,
  readOnly = false,
}) => {
  const { t } = useTranslation()
  const { addNotification } = useContext(AppContext)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState<boolean>(false)

  const attachInvoice = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]
    // Allows picking the same file again after a failed upload
    event.target.value = ''

    if (!file) {
      return
    }

    setLoading(true)
    void service
      .attachInvoice(disposition.id, file, addNotification, t)
      .then(success => success && onChanged())
      .finally(() => setLoading(false))
  }

  const sendInvoice = (): void => {
    setLoading(true)
    void service
      .sendInvoice(disposition.id, addNotification, t)
      .then(success => success && onChanged())
      .finally(() => setLoading(false))
  }

  const removeInvoice = (): Promise<unknown> =>
    service
      .removeInvoice(disposition.id, addNotification, t)
      .then(success => success && onChanged())

  if (!readOnly && service.canAttachInvoice(disposition)) {
    return (
      <div className='DispositionsList__invoice-actions'>
        <Button
          dataCy='invoice-attach'
          size={ButtonSize.SMALL}
          theme={ButtonTheme.BLUE}
          icon={faUpload}
          tooltip={t('agencies.dispositions.invoices.attach')}
          disabled={loading}
          onClick={() => fileInputRef.current?.click()}
        />
        <input
          ref={fileInputRef}
          type='file'
          accept='application/pdf'
          hidden
          onChange={attachInvoice}
        />
      </div>
    )
  }

  return (
    <div className='DispositionsList__invoice-actions'>
      <Button
        dataCy='invoice-download'
        size={ButtonSize.SMALL}
        theme={ButtonTheme.NAVY_BLUE_OUTLINE}
        icon={faDownload}
        tooltip={t('agencies.dispositions.downloadInvoice')}
        onClick={() =>
          service.downloadFile(disposition.invoiceFileUrl, service.invoiceFileName(disposition))
        }
      />
      {!readOnly && service.canSendInvoice(disposition) && (
        <Button
          dataCy='invoice-send'
          size={ButtonSize.SMALL}
          theme={ButtonTheme.NAVY_BLUE_OUTLINE}
          icon={faPaperPlane}
          tooltip={t('agencies.dispositions.invoices.send')}
          disabled={loading}
          onClick={sendInvoice}
        />
      )}
      {!readOnly && service.canRemoveInvoice(disposition) && (
        <>
          <Button
            dataCy='invoice-remove'
            size={ButtonSize.SMALL}
            theme={ButtonTheme.RED}
            icon={faTrashCan}
            tooltip={t('agencies.dispositions.invoices.remove')}
            disabled={loading}
            onClick={() => setIsRemoveModalOpen(true)}
          />
          <ModalAction
            title={t('agencies.dispositions.invoices.remove')}
            itemText={t('agencies.dispositions.invoices.removeConfirm')}
            actionText={t('common.remove')}
            isOpen={isRemoveModalOpen}
            setIsOpen={setIsRemoveModalOpen}
            isLoading={loading}
            setIsLoading={setLoading}
            onAction={removeInvoice}
          />
        </>
      )}
    </div>
  )
}

export default InvoiceActions
